<template>
  <!-- Profile Section -->

  <div v-if="mysmartcard">
    <!-- Banner Background -->
    <div class="profile-banner"></div>
    <div class="profile-header-img-wrapper">
      <div class="profile-img-container">
        <img :src="displayPhotoUrl" :alt="altText" class="profile-img" loading="lazy" @error="handleImageError"
          :class="{ 'error': imageLoadError }" />

      </div>
    </div>
    <div class="profile-body">
      <h1 style="font-size: 36px;">{{ fullName }}</h1>
      <template v-if="hasOrganization && mysmartcard.organizations?.[0]">
        <p class="profession">{{ mysmartcard.organizations[0]?.title || '' }}</p>
        <p class="company">{{ mysmartcard.organizations[0]?.company || '' }}</p>
      </template>

      <!-- Contact Info Section -->
      <div class="contact-info-section">
        <!-- Email -->
        <div v-if="firstEmail" class="contact-item clickable" @click="openEmailClient(firstEmail)"
          :title="`Send email to ${firstEmail}`">
          <Icon icon="mdi:email-outline" width="20" height="20" class="contact-icon" />
          <span>{{ firstEmail }}</span>
        </div>

        <!-- Phone and Address Row -->
        <div class="contact-row">
          <div v-if="firstPhone" class="contact-item clickable" @click="callPhone(firstPhone)"
            :title="`Call ${firstPhone}`">
            <Icon icon="mdi:phone-outline" width="20" height="20" class="contact-icon" />
            <span>{{ firstPhone }}</span>
          </div>

          <div v-if="firstAddress" class="contact-item clickable" @click="openMap(firstAddress)"
            :title="`View on map: ${firstAddress}`">
            <Icon icon="mdi:map-marker-outline" width="20" height="20" class="contact-icon" />
            <span>{{ firstAddressCountry }}</span>
          </div>
        </div>
      </div>


      <div class="social-icons" v-if="hasSocialMedia">
        <template v-for="(link, index) in socialLinks" :key="index">
          <a v-bind:href="link.url" target="_blank">
            <Icon :icon="link.icon" width="16" height="16" />
          </a>
        </template>
      </div>

      <div class="profile-footer">
        <div class="button-group">
          <button @click="handleDownload" class="btn" :disabled="isDownloading">
            <Icon :icon="isDownloading ? 'line-md:loading-twotone-loop' : 'line-md:downloading-loop'" width="24"
              height="24" style="color: black" />
            <span class="pl-1">{{ downloadButtonText }}</span>
          </button>

          <button @click="toggleQR" class="btn qr-btn">
            <Icon icon="mdi:qrcode" width="24" height="24" />
            <span class="pl-1">Scan QR</span>
          </button>
        </div>

        <!-- QR Code Modal -->
        <div v-if="showQR" class="qr-modal" @click.self="toggleQR">
          <div class="qr-modal-content">
            <button class="close-btn" @click="toggleQR" aria-label="Close">
              <Icon icon="mdi:close" width="24" height="24" />
            </button>
            <h3>Scan with your phone</h3>
            <img
              :src="'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + encodeURIComponent(currentUrl)"
              alt="QR Code" class="qr-code" />
            <p class="qr-instructions">Scan this QR code with your phone's camera to open this page</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Add the bottom sheet component -->
    <ContactBottomSheet :show="showContactSheet" :recipient-name="fullName"
      :card-share-id="mysmartcard._AppShare.cardIdShare" :fcm-token="mysmartcard._AppShare?.fcmToken"
      :document-id="mysmartcard.id || ''" :company-id="mysmartcard.companyId" @close="handleContactSheetClose"
      @submit-success="handleContactSubmitSuccess" @submit-error="handleContactSubmitError" />

    <!-- Get App Bottom Sheet -->
    <GetAppBottomSheet :show="showGetAppSheet" @close="showGetAppSheet = false" />
  </div>
</template>

<script setup lang="ts">
import { fetchPhotoAsBase64, toVCard, toVCardAndroid, toVCardAndroidMinimal } from "@/composables/getvCard";
import { Icon } from "@iconify/vue";
import defaultImage from "@/assets/images/abc.webp";
import { ref, computed, watchEffect, onUnmounted, onMounted } from "vue";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";
// @ts-ignore - Vue SFC default export
import ContactBottomSheet from "@/components/ContactBottomSheet.vue";
// @ts-ignore - Vue SFC default export
import GetAppBottomSheet from "@/components/GetAppBottomSheet.vue";
import { trackEvent } from "@/utils/analytics";
import type { QwikUCard } from "@/types";

type Props = {
  mysmartcard: QwikUCard;
  isVip: boolean;
};

type SocialMediaConfig = {
  [key: string]: { icon: string };
};

type SocialLink = {
  icon: string;
  url: string;
  label: string;
};

// Move constants outside component to prevent recreation
const storage = getStorage();
const SOCIAL_MEDIA_CONFIG: SocialMediaConfig = {
  X: { icon: 'ri:twitter-x-fill' },
  Twitter: { icon: 'ri:twitter-fill' },
  Facebook: { icon: 'ri:facebook-fill' },
  LinkedIn: { icon: 'ri:linkedin-fill' },
  Instagram: { icon: 'ri:instagram-fill' },
  TikTok: { icon: 'ri:tiktok-fill' },
  YouTube: { icon: 'ri:youtube-fill' },
  Telegram: { icon: 'ri:telegram-fill' },
  WhatsApp: { icon: 'ri:whatsapp-fill' },
  Snapchat: { icon: 'ri:snapchat-fill' },
  GitHub: { icon: 'ri:github-fill' },
  Pinterest: { icon: 'ri:pinterest-fill' },
  Reddit: { icon: 'ri:reddit-fill' },
  Discord: { icon: 'ri:discord-fill' },
  Twitch: { icon: 'ri:twitch-fill' },
  Custom: { icon: 'ri:link' }
};

// Global photo URL cache
const globalPhotoUrlCache = new Map<string, string>();

const props = defineProps<Props>();

const myphotoByte = ref<string | null>(null);
const isDownloading = ref(false);
const imageLoadError = ref(false);
const myphotoUrl = ref<string>(defaultImage);
const showQR = ref(false);
const currentUrl = ref('');
const showContactSheet = ref(false);
const showGetAppSheet = ref(false);

// Memoized photo fetching
const fetchPhotoUrl = async (photoUrl: string): Promise<string> => {
  if (!photoUrl) return defaultImage;

  if (globalPhotoUrlCache.has(photoUrl)) {
    return globalPhotoUrlCache.get(photoUrl) || defaultImage;
  }

  try {
    const imageRef = storageRef(storage, `userImages/${photoUrl}`);
    const url = await getDownloadURL(imageRef);
    globalPhotoUrlCache.set(photoUrl, url);
    return url;
  } catch (error) {
    console.error("Error fetching image URL:", error);
    return defaultImage;
  }
};

const openEmailClient = (emailAddress: string): void => {
  const mailtoLink = `mailto:${emailAddress}`
  window.open(mailtoLink, '_blank')
}

const callPhone = (phoneNumber: string): void => {
  const telLink = `tel:${phoneNumber}`
  window.open(telLink, '_self')
}

const openMap = (address: string): void => {
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  window.open(mapLink, '_blank')
}

// Optimized photo loading with cleanup
const loadPhoto = async (): Promise<void> => {
  if (!props.mysmartcard?.photoUrl) return;

  try {
    const url = await fetchPhotoUrl(props.mysmartcard.photoUrl);
    if (url !== defaultImage) {
      const photoData = await fetchPhotoAsBase64(url);
      myphotoUrl.value = photoData || defaultImage;
    }
  } catch (error) {
    console.error("Error loading photo:", error);
    myphotoUrl.value = defaultImage;
  }
};

// Watch for photo URL changes
watchEffect(() => {
  if (props.mysmartcard?.photoUrl) {
    loadPhoto();
  }
});

// Optimized computed properties
const displayPhotoUrl = computed((): string => {
  try {
    if (!myphotoUrl.value || imageLoadError.value) return defaultImage;

    if (myphotoUrl.value.startsWith('data:image/')) return myphotoUrl.value;
    if (myphotoUrl.value.match(/^[A-Za-z0-9+/=]+$/)) return `data:image/jpeg;base64,${myphotoUrl.value}`;
    if (myphotoUrl.value.startsWith('http')) return myphotoUrl.value;

    return defaultImage;
  } catch {
    return defaultImage;
  }
});

const fullName = computed((): string => {
  const { first = '', last = '' } = props.mysmartcard?.name || {};
  return `${first} ${last}`.trim();
});

const altText = computed((): string =>
  `${fullName.value}'s Profile Picture` || 'Profile Picture'
);

const hasOrganization = computed((): boolean =>
  (props.mysmartcard?.organizations?.length ?? 0) > 0
);

const firstEmail = computed((): string | null => {
  return props.mysmartcard?.emails?.[0]?.address || null;
});

const firstPhone = computed((): string | null => {
  return props.mysmartcard?.phones?.[0]?.number || null;
});

const firstAddress = computed((): string | null => {
  const addr = props.mysmartcard?.addresses?.[0];
  if (!addr) return null;

  // Construct address string
  if (addr.address) return addr.address;

  const parts = [
    addr.street,
    addr.city,
    addr.state,
    addr.postalCode,
    addr.country
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
});

const firstAddressCountry = computed((): string | null => {
  return props.mysmartcard?.addresses?.[0]?.country || null;
});

const hasSocialMedia = computed((): boolean =>
  (props.mysmartcard?.SmartContent?.some(c => c.contentType === 'socialContent') ?? false)
);

const socialLinks = computed((): SocialLink[] => {
  if (!props.mysmartcard?.SmartContent?.length) return [];

  return props.mysmartcard.SmartContent
    .filter(content => content.contentType === 'socialContent')
    .map((content) => {
      const url = content.contentUrl;
      // Infer platform from URL
      let platform = 'Custom';
      const lowerUrl = url.toLowerCase();

      for (const key of Object.keys(SOCIAL_MEDIA_CONFIG)) {
        if (key === 'Custom') continue;
        if (lowerUrl.includes(key.toLowerCase())) {
          platform = key;
          break;
        }
      }

      // Special cases for common shorteners or variations if needed
      if (platform === 'Custom') {
        if (lowerUrl.includes('t.me')) platform = 'Telegram';
        else if (lowerUrl.includes('fb.com')) platform = 'Facebook';
        else if (lowerUrl.includes('youtu.be')) platform = 'YouTube';
        else if (lowerUrl.includes('x.com')) platform = 'X';
        else if (lowerUrl.includes('wa.me')) platform = 'WhatsApp';
      }

      return {
        icon: SOCIAL_MEDIA_CONFIG[platform].icon,
        url: url,
        label: platform
      };
    });
});

const downloadButtonText = computed((): string =>
  isDownloading.value ? 'Downloading...' : 'Download My Contact'
);

const handleImageError = (e: Event): void => {
  imageLoadError.value = true;
  myphotoUrl.value = defaultImage;
  (e.target as HTMLImageElement).src = defaultImage;
};

// Detect if the device is Android
const isAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

const handleDownload = async (): Promise<void> => {
  isDownloading.value = true;

  try {
    // Track download event
    if (props.mysmartcard?.id && props.isVip) {
      trackEvent(props.mysmartcard.id, 'download', {}, props.mysmartcard.companyId);
    }

    // Validate required data
    if (!props.mysmartcard || !fullName.value) {
      throw new Error('Missing required contact information');
    }

    // Ensure we have either the photo data or default image
    const photoData = myphotoUrl.value === defaultImage ? null : myphotoUrl.value;

    // Choose vCard format based on device type for better compatibility
    let vCardData;
    if (isAndroid()) {
      console.log('Android device detected, using Android-optimized vCard format');
      try {
        // First try Android-optimized vCard format with photo
        vCardData = await toVCardAndroid(
          props.mysmartcard,
          photoData,
          {
            withPhoto: Boolean(photoData)
          }
        );
        console.log('Android vCard generated successfully with photo');
      } catch (error) {
        console.warn('Android vCard with photo failed, trying minimal version:', error);
        // Fallback to minimal vCard without photo for problematic Android devices
        vCardData = await toVCardAndroidMinimal(
          props.mysmartcard
        );
        console.log('Android minimal vCard generated successfully');
      }
    } else {
      console.log('Non-Android device detected, using standard vCard format');
      // Use standard vCard format for iOS and other devices
      vCardData = await toVCard(
        props.mysmartcard,
        photoData,
        {
          withPhoto: Boolean(photoData),
          encoding: 'base64'
        }
      );
      console.log('Standard vCard generated successfully');
    }

    console.log('Generated vCard size:', vCardData.length, 'characters');
    console.log('vCard preview:', vCardData.substring(0, 200) + '...');

    // Create and validate blob
    const blob = new Blob([vCardData], { type: "text/vcard;charset=utf-8" });
    if (blob.size === 0) {
      throw new Error('Generated vCard is empty');
    }

    // Create download link
    const url = URL.createObjectURL(blob);
    const filename = `${fullName.value.replace(/[^a-z0-9]/gi, '_')}_contact.vcf`;

    // Create and configure link element
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = filename;

    // Append, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup URL object
    setTimeout(() => URL.revokeObjectURL(url), 100);

    // Show the contact sheet after successful download
    showContactSheet.value = true;

  } catch (error: any) {
    console.error('Download failed:', error.message);
    throw new Error(`Failed to download contact: ${error.message}`);
  } finally {
    isDownloading.value = false;
  }
};

const handleContactSheetClose = () => {
  showContactSheet.value = false;
  // Show Get App sheet after a short delay for better UX
  setTimeout(() => {
    showGetAppSheet.value = true;
  }, 300);
};


const handleContactSubmitSuccess = (contactData: any): void => {
  console.log('Contact submitted successfully:', contactData);
  // You could show a success message here
};

const handleContactSubmitError = (error: string): void => {
  console.error('Error submitting contact:', error);
  // You could show an error message here
};

// Get current URL on mount
onMounted(() => {
  currentUrl.value = window.location.href;
});

const toggleQR = (): void => {
  showQR.value = !showQR.value;
};

// Cleanup
onUnmounted(() => {
  myphotoByte.value = null;
  myphotoUrl.value = defaultImage;
});
</script>

<style src="@/assets/styles/shared-styles.css"></style>

<style scoped>
.contact-info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
  color: #555;
}

.contact-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.contact-item.clickable {
  cursor: pointer;
  transition: color 0.2s;
}

.contact-item.clickable:hover {
  color: #000;
}

.contact-icon {
  color: #666;
}

.profile-banner {
  width: 100%;
  height: 120px;
  background: url('~@/assets/images/Ft.webp') top center/cover no-repeat;
  position: relative;
  z-index: 1;
}

.profile-header-img-wrapper {
  display: flex;
  justify-content: center;
  position: relative;
  margin-top: -45px;
  z-index: 2;
}

.profile-img-container {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
  display: flex;
  align-items: center;
  justify-content: center;
}



.profile-img {
  width: 95px;
  height: 95px;
  object-fit: cover;
  border-radius: 50%;
  border: 0px solid #fff;
  background: #f3f4f6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.social-icons {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  align-items: center;
  margin: 0.75rem 0;
}

.social-icons a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  color: #333;
  transition: all 0.3s ease;
  text-decoration: none;
}

.social-icons a:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.social-icons a svg {
  width: 16px;
  height: 16px;
}

.button-group {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:hover {
  background: #f5f5f5;
}

.qr-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
}

.qr-modal-content {
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  position: relative;
  text-align: center;
  max-width: 90%;
  width: 300px;
  cursor: default;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  color: #666;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #333;
}

.qr-code {
  margin: 1rem 0;
  max-width: 200px;
  height: auto;
}

.qr-instructions {
  font-size: 0.9rem;
  color: #666;
  margin-top: 1rem;
}

.profession,
.company {
  width: 100%;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: center;
  padding: 0 1rem;
  box-sizing: border-box;
}
</style>
